export type InternationalPhoneLocale = {
  countryCodes: string[];
  placeholders: Record<string, string>;
};

export type InternationalPhoneLocaleDelta = {
  omit?: {
    countryCodes?: string[];
  };
  extend?: {
    countryCodes?: string[];
    placeholders?: Partial<Record<string, string>>;
  };
};

const normalizeCode = (code: string) => code.toLowerCase();

function dedupeCountryCodes(codes: string[]): string[] {
  const seen = new Set<string>();
  return codes.filter((c) => {
    const k = normalizeCode(c);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

export function mergeInternationalPhoneLocale(
  base: InternationalPhoneLocale,
  delta: InternationalPhoneLocaleDelta = {}
): InternationalPhoneLocale {
  const toOmit = delta.omit?.countryCodes ?? [];
  const omitSet = new Set(toOmit.map(normalizeCode));

  const countryCodes = dedupeCountryCodes([
    ...base.countryCodes.filter((c) => !omitSet.has(normalizeCode(c))),
    ...(delta.extend?.countryCodes ?? []),
  ]);

  const mergedPlaceholders = { ...base.placeholders };
  for (const code of toOmit) {
    delete mergedPlaceholders[normalizeCode(code)];
  }
  Object.assign(mergedPlaceholders, delta.extend?.placeholders ?? {});

  const placeholders: Record<string, string> = {};
  for (const code of countryCodes) {
    const key = normalizeCode(code);
    const val = mergedPlaceholders[key];
    if (val !== undefined) placeholders[key] = val;
  }

  return { countryCodes, placeholders };
}

/** Shared country list and placeholders used by search and FraudScan variants. */
export const INTERNATIONAL_PHONE_LOCALE_BASE: InternationalPhoneLocale = {
  countryCodes: [
    'in',
    'ca',
    'mx',
    'pe',
    'id',
    'th',
    'ar',
    'ph',
    'co',
    'tr',
    'cl',
    'il',
    'us',
    'uy',
    'py',
    'my',
    'za',
    'sg',
    'kz',
    'do',
    'ec',
    'ge',
    'na',
    'kw',
    'mv',
    'bs',
    'bt',
    'ba',
    'bn',
    'bf',
    'bi',
    'kh',
    'td',
    'km',
    'dj',
    'gm',
    'gt',
    'sv',
    'gn',
    'gw',
    'gy',
    'jo',
    'kg',
    'la',
    'ls',
    'mz',
    'np',
    'pr',
    'sl',
    'sr',
    'tw',
    'tj',
    'tl',
    'tt',
    'ae',
    'vi',
  ],
  placeholders: {
    in: '74104 10123',
    ca: '506-234-5678',
    mx: '200 123 4567',
    pe: '123 456 789',
    id: '123 4567 8910',
    th: '12 345 6789',
    ar: '11 2345-6789',
    ph: '123 456 7890',
    co: '601 234 5678',
    tr: '212 345 6789',
    cl: '2 2123 4567',
    il: '123 4567',
    us: '201 555 0123',
    uy: '2901 2345',
    py: '021 234 567',
    my: '03 1234 5678',
    za: '021 123 4567',
    sg: '6123 4567',
    kz: '727 123 4567',
    do: '809 123 4567',
    ec: '02 345 6789',
    ge: '012345678',
    na: '012345678',
    kw: '01234567',
    mv: '0123456',
    bs: '242 359 1234',
    bt: '1712 3456',
    ba: '61 123 456',
    bn: '712 3456',
    bf: '70 12 34 56',
    bi: '79 12 34 56',
    kh: '12 345 678',
    td: '63 12 34 56',
    km: '321 23 45',
    dj: '77 83 12 34',
    gm: '301 2345',
    gt: '5123 4567',
    sv: '7123 4567',
    gn: '622 12 34 56',
    gw: '955 123 456',
    gy: '623 1234',
    jo: '7 9012 3456',
    kg: '700 123 456',
    la: '20 9123 4567',
    ls: '5012 3456',
    mz: '84 123 4567',
    np: '9812345678',
    pr: '787 234 5678',
    sl: '76 123456',
    sr: '741 2345',
    tw: '912 345 678',
    tj: '917 12 34 56',
    tl: '7721 2345',
    tt: '868 291 1234',
    ae: '50 123 4567',
    vi: '340 642 1234',
  },
};

/**
 * - `omit.countryCodes` — ISO codes to remove from the base list (and drop their placeholders).
 * - `extend.countryCodes` — extra ISO codes appended after the base (minus omitted).
 * - `extend.placeholders` — placeholders for extended codes, or overrides for codes still in the list.
 */
export const INTERNATIONAL_PHONE_SEARCH_DELTA: InternationalPhoneLocaleDelta =
  {};

export const INTERNATIONAL_PHONE_FRAUD_SCAN_DELTA: InternationalPhoneLocaleDelta =
  {
    extend: {
      countryCodes: [
        'br',
        'al',
        'dz',
        'ao',
        'am',
        'aw',
        'cw',
        'au',
        'az',
        'bh',
        'bd',
        'bj',
        'bm',
        'bw',
        'vg',
        'cv',
        'cm',
        'ky',
        'cx',
        'cc',
        'cr',
        'eg',
        'gq',
        'sz',
        'fo',
        'ga',
        'gh',
        'gi',
        'hm',
        'is',
        'im',
        'jm',
        'jp',
        'je',
        'gg',
        'ke',
        'li',
        'mg',
        'mw',
        'mr',
        'mu',
        'md',
        'mc',
        'mn',
        'me',
        'ma',
        'ne',
        'mk',
        'no',
        'om',
        'pa',
        'qa',
        'rw',
        'pm',
        'st',
        'sa',
        'sn',
        'rs',
        'sc',
        'sx',
        'kr',
        'ch',
        'tz',
        'tg',
        'tn',
        'ug',
        'uz',
        'vn',
        'zm',
        'gb',
      ],
      placeholders: {
        br: '11 91234-5678',
        al: '67 123 4567',
        dz: '555 12 34 56',
        ao: '923 123 456',
        am: '91 123456',
        aw: '560 1234',
        cw: '9 512 3456',
        au: '412 345 678',
        az: '50 123 45 67',
        bh: '3600 1234',
        bd: '1712 345678',
        bj: '90 12 34 56',
        bm: '441 300 1234',
        bw: '71 123 456',
        vg: '284 300 1234',
        cv: '991 12 34',
        cm: '6 71 23 45 67',
        ky: '345 323 1234',
        cx: '8 9164 1234',
        cc: '8 9162 1234',
        cr: '6123 4567',
        eg: '100 123 4567',
        gq: '222 123 456',
        sz: '7612 3456',
        fo: '211234',
        ga: '06 12 34 56',
        gh: '24 123 4567',
        gi: '56001234',
        hm: '1 12345',
        is: '611 1234',
        im: '7624 123456',
        jm: '876 210 1234',
        jp: '90 1234 5678',
        je: '7797 123456',
        gg: '7781 123456',
        ke: '712 123456',
        li: '660 123456',
        mg: '32 12 345 67',
        mw: '991 23 45 67',
        mr: '36 12 34 56',
        mu: '5251 1234',
        md: '621 12345',
        mc: '6 12 34 56 78',
        mn: '8812 3456',
        me: '67 123 456',
        ma: '612 34 56 78',
        ne: '93 12 34 56',
        mk: '70 123 456',
        no: '412 34 567',
        om: '9123 4567',
        pa: '6123 4567',
        qa: '3312 3456',
        rw: '788 123 456',
        pm: '55 12 34',
        st: '981 2345',
        sa: '50 123 4567',
        sn: '77 123 45 67',
        rs: '60 1234567',
        sc: '251 2345',
        sx: '721 520 1234',
        kr: '10 1234 5678',
        ch: '79 123 45 67',
        tz: '712 123 456',
        tg: '90 12 34 56',
        tn: '20 123 456',
        ug: '712 123456',
        uz: '90 123 45 67',
        vn: '912 345 678',
        zm: '97 1234567',
        gb: '07123 456789',
      },
    },
  };

export const internationalPhoneSearchLocale = mergeInternationalPhoneLocale(
  INTERNATIONAL_PHONE_LOCALE_BASE,
  INTERNATIONAL_PHONE_SEARCH_DELTA
);

export const internationalPhoneFraudSearchLocale =
  mergeInternationalPhoneLocale(
    INTERNATIONAL_PHONE_LOCALE_BASE,
    INTERNATIONAL_PHONE_FRAUD_SCAN_DELTA
  );
